/**
 * ItemContextMenuHost: the one context menu a workspace view mounts, opened
 * from any of its items by `useItemContextMenu`. It acts on
 * `flatItems ∩ targetIds` (pass the view's loaded rows as `items`), and it
 * closes on its own if a target disappears (another client deleted or moved
 * it), since acting on the rest would not be what the user asked for.
 *
 * Escape (or any choice) closes the menu and returns focus to the item that
 * opened it (or, if that item is gone, to the item that took its place in
 * the view). An outside click that focused another control leaves focus there. The menu unmounts on close, so Chakra's own focus return (to
 * the invisible anchor) never runs and cannot take focus back.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { WorkspaceItem } from '@bower/shared';

import { ItemContextMenu } from './ItemContextMenu';
import type { ItemSurface } from '../../../stores/selectionStore';
import { closeItemMenu, useItemMenuStore } from './itemMenuStore';
import { useItemMenuActions } from './useItemMenuActions';

interface ItemContextMenuHostProps {
  /** The view's loaded rows (`flatItems`). */
  readonly items: readonly WorkspaceItem[];
  /** Which opens this host renders (the sidebar tree mounts its own). */
  readonly surface?: ItemSurface;
  /** Show Open and Delete's keys: desktop canvas views only. */
  readonly shortcutHints?: boolean;
  /**
   * Rename in place (the view draws InlineRenameField); false = in a dialog,
   * for views with no inline name editor (the phone list and grid).
   */
  readonly inlineRename?: boolean;
}

export function ItemContextMenuHost({
  items,
  surface = 'canvas',
  shortcutHints = false,
  inlineRename = true,
}: ItemContextMenuHostProps) {
  const targetIds = useItemMenuStore((s) => s.targetIds);
  const isMine = useItemMenuStore((s) => s.surface === surface);
  // Mounted while there are targets, not only while the menu is open: a
  // dialog started from the menu (Delete, Move) outlives the menu itself.
  return targetIds.length === 0 || !isMine ? null : (
    <HostedMenu
      items={items}
      targetIds={targetIds}
      shortcutHints={shortcutHints}
      renameSurface={inlineRename ? surface : null}
    />
  );
}

function HostedMenu({
  items,
  targetIds,
  shortcutHints,
  renameSurface,
}: {
  items: readonly WorkspaceItem[];
  targetIds: readonly string[];
  shortcutHints: boolean;
  renameSurface: ItemSurface | null;
}) {
  const anchor = useItemMenuStore((s) => s.anchor);
  const openId = useItemMenuStore((s) => s.openId);
  const targets = useMemo(() => {
    const ids = new Set(targetIds);
    return items.filter((item) => ids.has(item.id));
  }, [items, targetIds]);
  const actions = useItemMenuActions(targets, renameSurface);

  return (
    <>
      {anchor && (
        <OpenMenu key={openId} targetCount={targets.length}>
          <ItemContextMenu
            trigger={{
              kind: 'point',
              ...anchor,
              onClose: () => closeItemMenu({ returnFocus: !focusLeftMenu() }),
            }}
            entries={actions.entries}
            isFavorite={actions.isFavorite}
            shortcutHints={shortcutHints}
            addSubmenu={actions.addSubmenu}
            accessItem={actions.accessItem}
          />
        </OpenMenu>
      )}
      {actions.dialogs}
    </>
  );
}

/**
 * Did the close come from the user moving focus elsewhere (an outside click
 * on a control)? Then focus stays where they put it, as Chakra's own focus
 * return does. Escape, keyboard and chosen actions close with focus still in
 * the menu (or on <body> once it unmounts), and those return focus.
 */
function focusLeftMenu(): boolean {
  const active = document.activeElement;
  return active !== null && active !== document.body && !active.closest('[role="menu"]');
}

/**
 * One open of the menu (keyed per open). It remembers how many targets were
 * on screen when it opened, so a selection id that was never loaded does not
 * count as lost, and closes once any of those targets leaves the view. Focus
 * was in the menu, so it goes back to the view rather than to the page.
 */
function OpenMenu({ targetCount, children }: { targetCount: number; children: ReactNode }) {
  const [openedWith] = useState(targetCount);
  const lost = targetCount < openedWith;
  useEffect(() => {
    if (lost) closeItemMenu({ returnFocus: true });
  }, [lost]);
  return lost ? null : children;
}
